import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaCheckCircle, FaChartBar, FaEye, FaVolumeUp, FaPencilAlt, FaWalking } from 'react-icons/fa'; 

const SummaryStyle = () => {
    // ... (State dan Logic tetap sama) ...
    const [loading, setLoading] = useState(true);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const student = JSON.parse(localStorage.getItem("student"));
    const studentId = student?.student_id;

    const styleDetails = {
        V: { 
            name: 'Visual', 
            icon: FaEye, 
            description: 'Learns best by seeing: diagrams, charts, videos, and visual aids. They need to see the information.',
            color: 'text-indigo-600'
        },
        A: { 
            name: 'Aural', 
            icon: FaVolumeUp, 
            description: 'Learns best by hearing: discussions, lectures, recordings, and reading aloud. They need to hear the information.',
            color: 'text-green-600'
        },
        T: { 
            name: 'Read/Write (Textual)', 
            icon: FaPencilAlt, 
            description: 'Learns best through words: reading texts, writing notes, and creating lists. They need to process the information through language.',
            color: 'text-red-600'
        },
        K: { 
            name: 'Kinesthetic', 
            icon: FaWalking, 
            description: 'Learns best by doing: using hands-on experience, movement, practice, and real-life examples. They need to experience the information.',
            color: 'text-orange-600'
        },
    };

    const fetchLearningStyleResult = async (id) => {
        if (!id || Number(id) <= 0) {
            setError("Student session error or ID missing.");
            setLoading(false);
            return;
        }
        // ... (Logic fetch data tetap sama) ...
        try {
            const res = await fetch(`http://localhost:3000/api/learning-style/result?student_id=${id}`);
            const data = await res.json();
            
            if (!res.ok) {
                throw new Error(data.message || "Failed to fetch result.");
            }

            setResult(data);
            
            const currentStudent = JSON.parse(localStorage.getItem("student"));
            if (currentStudent) {
                 localStorage.setItem("student", JSON.stringify({...currentStudent, ls_current_step: 'done'}));
            }

        } catch (err) {
            console.error(err);
            setError("Failed to load results. Please try again later. Detail: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLearningStyleResult(studentId);
    }, [studentId]);


    // ... (Loading dan Error state tetap sama) ...
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-blue-900 text-white">
                <p className="text-xl">Calculating results... Please wait.</p>
            </div>
        );
    }

    if (error || !result) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-blue-900 text-white p-4">
                <p className="text-xl text-red-400 mb-4">{error}</p>
                <button 
                    onClick={() => window.location.href = '/dashboard'}
                    className="bg-yellow-500 text-blue-900 font-bold px-6 py-2 rounded-full shadow-lg"
                >
                    Go to Dashboard
                </button>
            </div>
        );
    }

    const { dominant_style, style_code, percentage, scores } = result;

    return (
        // Menyesuaikan padding vertikal (py-4) untuk tampilan lebih compact
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-blue-900 via-blue-700 to-blue-500 text-white px-4 py-4"> 
            
            <motion.div 
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-5xl bg-white text-blue-900 rounded-2xl shadow-2xl p-6 md:p-8"
            >
                <div className="text-center mb-4"> {/* Mengurangi mb-6 menjadi mb-4 */}
                    
                    {/* >>> PERUBAHAN: FA AWARD DIHAPUS DI SINI <<< 
                    <FaAward className="mx-auto text-yellow-500 text-5xl mb-3" /> */} 

                    <h1 className="text-3xl font-extrabold text-blue-800">Learning Style Result</h1>
                    <p className="text-md text-gray-600">Congratulations! You have completed the questionnaire.</p>
                </div>

                <hr className="mb-6" />

                {/* CONTAINER UTAMA COMPACT */}
                <div className="flex flex-col md:flex-row md:space-x-6">
                    
                    {/* DOMINANT STYLE CARD (1/3 lebar) */}
                    <motion.div 
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="bg-blue-100 p-6 rounded-xl border-4 border-yellow-500 mb-6 md:mb-0 text-center md:w-1/3 flex flex-col justify-center"
                    >
                        <h2 className="text-xl font-semibold mb-2">Your Dominant Style:</h2>
                        <motion.p
                            initial={{ scale: 0.8 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 100 }}
                            className={`text-4xl md:text-5xl font-black ${styleDetails[style_code]?.color || 'text-yellow-700'} my-4`}
                        >
                            {dominant_style} ({style_code})
                        </motion.p>
                        <p className="text-sm text-gray-700">This preference represents {percentage}% of your VAKT score.</p>
                    </motion.div>

                    {/* SCORE BREAKDOWN TABLE (2/3 lebar) */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        className="md:w-2/3"
                    >
                        <h2 className="text-xl font-bold mb-3 flex items-center text-blue-800">
                            <FaChartBar className="mr-2 text-yellow-500" /> VAKT Score Breakdown
                        </h2>
                        
                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md text-sm">
                                {/* ... (Tabel tetap sama) ... */}
                                <thead>
                                    <tr className="bg-blue-800 text-white">
                                        <th className="py-2 px-3 text-left">Code</th>
                                        <th className="py-2 px-3 text-left">Style</th>
                                        <th className="py-2 px-3 text-right">Score</th>
                                        <th className="py-2 px-3 text-right">Preference (%)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scores && scores.map((s, index) => (
                                        <tr key={s.style} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                            <td className="py-2 px-3 font-semibold">{s.style}</td>
                                            <td className="py-2 px-3">
                                                {styleDetails[s.style]?.name || 'Unknown'}
                                            </td>
                                            <td className="py-2 px-3 text-right">{s.score}</td>
                                            <td className="py-2 px-3 text-right">
                                                <div className="flex items-center justify-end">
                                                    <span className="mr-2 font-bold text-xs">{s.percent}%</span>
                                                    <div className="w-16 bg-gray-200 rounded-full h-2">
                                                        <div 
                                                            className="bg-yellow-500 h-2 rounded-full" 
                                                            style={{ width: `${s.percent}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </motion.div>
                </div>
                {/* END CONTAINER UTAMA COMPACT */}
                
                <hr className="my-6 border-gray-200" />
                
                {/* DESKRIPSI GAYA BELAJAR (Compact List) */}
                <h2 className="text-xl font-bold mb-4 text-blue-800">What Does Each Style Mean?</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {Object.values(styleDetails).map((detail) => (
                        <div key={detail.name} className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <detail.icon className={`text-2xl mr-3 ${detail.color} flex-shrink-0 mt-1`} />
                            <div>
                                <span className="font-bold text-base text-gray-800">{detail.name}:</span> {detail.description}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="text-center mt-8">
                    <button 
                        onClick={() => window.location.href = '/dashboard'}
                        className="bg-blue-800 hover:bg-blue-700 text-white font-bold px-12 py-3 rounded-full shadow-lg transition duration-300 flex items-center mx-auto"
                    >
                        <FaCheckCircle className="mr-2" /> Finish and Go to Dashboard
                    </button>
                </div>

            </motion.div>
            
            <p className="mt-4 text-blue-200 text-xs"> 
                © 2025 Universitas Terbuka
            </p>

        </div>
    );
};

export default SummaryStyle;